// School-admin + teacher API surface (apps/app). Wraps /schools/* and
// /teacher/*. docs/replan-v2.md R9.

import { api } from './api';
import type { KidProgress } from './learn';

export type Grade = 'kg2' | 'g1' | 'g2' | 'g3' | 'g4' | 'g5' | 'g6';

// ─── School admin ────────────────────────────────────────────────────────────

export interface School {
  id: string;
  name: string;
  slug: string;
  locale: 'ar' | 'en';
  status: string;
}

export interface SchoolRegisterInput {
  email: string;
  password: string;
  adminDisplayName: string;
  schoolName: string;
  country: string;
  locale: 'ar' | 'en';
  marketingOptIn?: boolean;
}

export async function registerSchool(input: SchoolRegisterInput): Promise<School> {
  const { school } = await api<{ school: School }>('/schools/register', {
    method: 'POST',
    body: input,
  });
  return school;
}

export async function getMySchool(signal?: AbortSignal): Promise<School> {
  const { school } = await api<{ school: School }>('/schools/me', { signal });
  return school;
}

// ─── Teachers ────────────────────────────────────────────────────────────────

export interface Teacher {
  id: string;
  email: string | null;
  displayName: string | null;
  status: 'active' | 'invited';
  invitedAt: string | null;
  joinedAt: string | null;
}

export async function listTeachers(signal?: AbortSignal): Promise<Teacher[]> {
  const { teachers } = await api<{ teachers: Teacher[] }>('/schools/teachers', { signal });
  return teachers;
}

export async function inviteTeacher(email: string, displayName: string): Promise<void> {
  await api('/schools/teachers/invite', { method: 'POST', body: { email, displayName } });
}

export async function removeTeacher(id: string): Promise<void> {
  await api(`/schools/teachers/${id}`, { method: 'DELETE' });
}

export interface TeacherInvitePreview {
  schoolName: string;
  email: string;
  valid: boolean;
}

export async function previewTeacherInvite(token: string): Promise<TeacherInvitePreview> {
  const { preview } = await api<{ preview: TeacherInvitePreview }>(
    `/schools/invite/teacher?token=${encodeURIComponent(token)}`,
  );
  return preview;
}

export async function acceptTeacherInvite(
  token: string,
  password: string,
  displayName?: string,
): Promise<void> {
  await api('/schools/invite/teacher/accept', {
    method: 'POST',
    body: { token, password, displayName },
  });
}

// ─── Students ────────────────────────────────────────────────────────────────

export interface Student {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  grade: string | null;
  localePref: 'ar' | 'en';
  createdAt: string;
}

export async function listStudents(signal?: AbortSignal): Promise<Student[]> {
  const { students } = await api<{ students: Student[] }>('/schools/students', { signal });
  return students;
}

export interface CreateStudentInput {
  username: string;
  password: string;
  displayName: string;
  grade?: Grade | null;
  classId?: string | null;
}

export async function createStudent(input: CreateStudentInput): Promise<Student> {
  const { student } = await api<{ student: Student }>('/schools/students', {
    method: 'POST',
    body: input,
  });
  return student;
}

export async function resetStudentPassword(id: string, password: string): Promise<void> {
  await api(`/schools/students/${id}/reset-password`, { method: 'POST', body: { password } });
}

export async function deleteStudent(id: string): Promise<void> {
  await api(`/schools/students/${id}`, { method: 'DELETE' });
}

// ─── Classes ─────────────────────────────────────────────────────────────────

export interface SchoolClass {
  id: string;
  name: string;
  grade: string;
  teacher: { id: string; displayName: string | null } | null;
  studentCount: number;
}

export async function listClasses(signal?: AbortSignal): Promise<SchoolClass[]> {
  const { classes } = await api<{ classes: SchoolClass[] }>('/schools/classes', { signal });
  return classes;
}

export interface CreateClassInput {
  name: string;
  grade: Grade;
  teacherUserId?: string | null;
}

export async function createClass(input: CreateClassInput): Promise<SchoolClass> {
  const { class: cls } = await api<{ class: SchoolClass }>('/schools/classes', {
    method: 'POST',
    body: input,
  });
  return cls;
}

export async function updateClass(
  id: string,
  input: { name?: string; grade?: Grade; teacherUserId?: string | null },
): Promise<SchoolClass> {
  const { class: cls } = await api<{ class: SchoolClass }>(`/schools/classes/${id}`, {
    method: 'PATCH',
    body: input,
  });
  return cls;
}

export async function deleteClass(id: string): Promise<void> {
  await api(`/schools/classes/${id}`, { method: 'DELETE' });
}

export async function addStudentToClass(classId: string, userId: string): Promise<void> {
  await api(`/schools/classes/${classId}/students`, { method: 'POST', body: { userId } });
}

// ─── Teacher dashboard ───────────────────────────────────────────────────────

export interface TeacherClass {
  id: string;
  name: string;
  grade: string;
  studentCount: number;
}

export async function listTeacherClasses(signal?: AbortSignal): Promise<TeacherClass[]> {
  const { classes } = await api<{ classes: TeacherClass[] }>('/teacher/classes', { signal });
  return classes;
}

export interface ClassStudent {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  grade: string | null;
  lessonsCompleted: number;
  xp: number;
}

export async function listClassStudents(
  classId: string,
  signal?: AbortSignal,
): Promise<ClassStudent[]> {
  const { students } = await api<{ students: ClassStudent[] }>(
    `/teacher/classes/${classId}/students`,
    { signal },
  );
  return students;
}

export interface AssignableLesson {
  id: string;
  titleKey: string;
  subjectSlug: string;
  grade: string;
}

export async function listAssignableLessons(signal?: AbortSignal): Promise<AssignableLesson[]> {
  const { lessons } = await api<{ lessons: AssignableLesson[] }>('/teacher/lessons', { signal });
  return lessons;
}

export interface Assignment {
  id: string;
  classId: string;
  className?: string;
  lessonId: string;
  lessonTitleKey: string;
  dueAt: string | null;
  createdAt: string;
}

export async function listTeacherAssignments(signal?: AbortSignal): Promise<Assignment[]> {
  const { assignments } = await api<{ assignments: Assignment[] }>('/teacher/assignments', {
    signal,
  });
  return assignments;
}

export async function createAssignment(input: {
  classId: string;
  lessonId: string;
  dueAt?: string | null;
}): Promise<Assignment> {
  const { assignment } = await api<{ assignment: Assignment }>('/teacher/assignments', {
    method: 'POST',
    body: input,
  });
  return assignment;
}

export async function deleteAssignment(id: string): Promise<void> {
  await api(`/teacher/assignments/${id}`, { method: 'DELETE' });
}

// Teacher views a student's progress — same shape as the parent dashboard.
export async function getStudentProgress(
  studentId: string,
  signal?: AbortSignal,
): Promise<KidProgress> {
  const { progress } = await api<{ progress: KidProgress }>(
    `/teacher/students/${studentId}/progress`,
    { signal },
  );
  return progress;
}

// ─── Student-side assigned work (kid shell) ──────────────────────────────────

export interface LearnerAssignment {
  id: string;
  lessonId: string;
  lessonTitleKey: string;
  className: string;
  dueAt: string | null;
  completed: boolean;
}

export async function listMyAssignments(signal?: AbortSignal): Promise<LearnerAssignment[]> {
  const { assignments } = await api<{ assignments: LearnerAssignment[] }>('/kids/assignments', {
    signal,
  });
  return assignments;
}
